import { mergeOptions } from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.constants';
import { LocationOptionType } from 'components/LocationFilterInput/LocationFilterInput.types';
import { useSSFBehaviors } from 'hooks';
import { nonPaginatedApolloClient } from 'providers/ApolloClients/NonPaginatedApolloClient';
import { QUERY_fetchSecondaryCommunities } from 'queries';
import { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FetchSecondaryCommunitiesQuery } from 'types/graphql';

export const SecondaryLocationFilterDataContext = createContext({
  dropdownData: [] as LocationOptionType[],
  hasNextPage: false,
  isLoading: true,
});

const client = nonPaginatedApolloClient();

export const SecondaryLocationFilterDataProvider: FC<{
  withEnabledFeatures: string[];
  withoutEnabledFeatures: string[];
  selectedPrimaryLocationId?: string;
}> = ({ withEnabledFeatures, withoutEnabledFeatures, selectedPrimaryLocationId, children }) => {
  const [hasReceivedData, setHasReceivedData] = useState(false);
  const [, { data, isLoading, loadMore }] = useSSFBehaviors<
    { options: LocationOptionType[]; hasNextPage: boolean },
    LocationOptionType[]
  >({
    key: 'LOCATION_FILTER_INPUT_B2B',
    query: QUERY_fetchSecondaryCommunities,
    client,
    variables: {
      byName: '',
      withEnabledFeatures,
    },
    schema: {
      byFullName: {
        initialValue: '',
        mapper: String,
      },
      withEnabledFeatures: {
        initialValue: withEnabledFeatures,
      },
      withoutEnabledFeatures: {
        initialValue: withoutEnabledFeatures,
      },
    },
    pageSize: 20,
    debounceTimeout: 500,
    paginationType: 'InfiniteScroll',
    responseTransformer: (data?: FetchSecondaryCommunitiesQuery) => {
      if (!data) return [];
      const mapped: LocationOptionType[] = data?.fetchCommunities?.nodes
        ? data.fetchCommunities.nodes.map(({ id, name, location }) => {
            const stringified = id.toString();
            return {
              name: 'location',
              label: name,
              key: stringified,
              value: stringified,
              id: stringified,
              type: 'Standard',
              timeZone: location?.timeZone || undefined,
            };
          })
        : [];

      return {
        options: mapped,
        hasNextPage: data?.fetchCommunities?.pageInfo?.hasNextPage || false,
      };
    },
  });

  const [dropdownData, setDropdownData] = useState<LocationOptionType[]>([]);
  const prevOptionsRef = useRef<LocationOptionType[]>([]);

  useEffect(() => {
    if (
      data?.options &&
      prevOptionsRef.current !== data?.options &&
      JSON.stringify(prevOptionsRef.current) !== JSON.stringify(data?.options)
    ) {
      setHasReceivedData(true);
      setDropdownData((currentData) => mergeOptions(currentData, data.options));
      prevOptionsRef.current = data.options;
    }
  }, [data?.options, selectedPrimaryLocationId]);

  useEffect(() => {
    if (data?.hasNextPage && !isLoading) {
      loadMore();
    }
  }, [data?.hasNextPage, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => {
    return {
      dropdownData: (dropdownData || ([] as LocationOptionType[])).filter(
        (dropdownOption) => dropdownOption.value !== selectedPrimaryLocationId,
      ),
      hasNextPage: !!data?.hasNextPage,
      isLoading: isLoading || !hasReceivedData,
    };
  }, [data?.hasNextPage, dropdownData, hasReceivedData, isLoading, selectedPrimaryLocationId]);

  return (
    <SecondaryLocationFilterDataContext.Provider value={value}>
      {children}
    </SecondaryLocationFilterDataContext.Provider>
  );
};

export const useSecondaryLocationFilterDataContext = () =>
  useContext(SecondaryLocationFilterDataContext);
